import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Icon from '../components/Icon';
import { apiFetch } from '../api';

// Bo'limlar ro'yxat tartibini saqlaydi: kartalar ketma-ket guruhlanadi.
const MENU_ITEMS = [
  {
    path: '/admin/users',
    title: 'Foydalanuvchilar',
    desc: "Ro'yxat, qidiruv, blok/unblok",
    icon: 'users',
    section: 'Foydalanuvchilar',
  },
  {
    path: '/admin/moderation',
    title: 'Ustalar moderatsiyasi',
    desc: 'Kutilayotgan ustalarni tasdiqlash yoki rad etish',
    icon: 'shield',
    stat: 'pendingMastersCount',
    statLabel: 'kutilmoqda',
    section: 'Foydalanuvchilar',
  },
  {
    path: '/admin/service-categories',
    title: 'Xizmat kategoriyalari',
    desc: "Xizmat turlarini qo'shish va boshqarish",
    icon: 'folder',
    section: 'Xizmatlar',
  },
  {
    path: '/admin/providers',
    title: "Xizmat ko'rsatuvchilar",
    desc: "Ko'rsatuvchilarni ro'yxatdan o'tkazish va boshqarish",
    icon: 'wrench',
    section: 'Xizmatlar',
  },
  {
    path: '/admin/orders',
    title: 'Buyurtmalar monitoringi',
    desc: 'Real vaqtli statistika va buyurtmalar',
    icon: 'package',
    stat: 'total',
    statLabel: 'jami',
    section: 'Buyurtmalar',
  },
  {
    path: '/admin/disputes',
    title: 'Nizolar',
    desc: 'Nizolarni ko\'rib chiqish va hal qilish',
    icon: 'scale',
    stat: 'disputed',
    statLabel: 'e\'tiroz bildirilgan',
    section: 'Buyurtmalar',
  },
  {
    path: '/admin/notifications',
    title: 'Push xabarlar',
    desc: 'Foydalanuvchilarga bildirishnoma yuborish',
    icon: 'bell',
    section: 'Xabarlar va moliya',
  },
  {
    path: '/admin/commission',
    title: 'Narx sozlamalari',
    desc: 'Komissiya, Prime obuna va boshqa summalar',
    icon: 'percent',
    section: 'Xabarlar va moliya',
  },
  {
    path: '/admin/reports',
    title: 'Moliyaviy hisobot',
    desc: 'Kunlik / haftalik / oylik daromad',
    icon: 'barChart',
    section: 'Xabarlar va moliya',
  },
];

const SECTIONS = MENU_ITEMS.reduce((acc, item) => {
  const last = acc[acc.length - 1];
  if (last && last.title === item.section) last.items.push(item);
  else acc.push({ title: item.section, items: [item] });
  return acc;
}, []);

export default function AdminDashboard() {
  const { auth } = useAuth();
  const navigate = useNavigate();

  // Sanoqlar: GET /api/admin/orders/stats -> { total, disputed, pendingMastersCount, ... }.
  // Yuklanguncha `stats === undefined` (loader), xatoda yoki maydon yo'q bo'lsa 0.
  const [stats, setStats] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/admin/orders/stats', auth?.token);
        const data = res.ok ? await res.json() : null;
        if (!cancelled) setStats(data && typeof data === 'object' ? data : null);
      } catch {
        if (!cancelled) setStats(null);
      }
    })();
    return () => { cancelled = true; };
  }, [auth?.token]);

  const fullName = auth?.user
    ? [auth.user.firstName, auth.user.lastName].filter(Boolean).join(' ')
    : 'Admin';

  return (
    <AdminLayout>
      <h1 style={s.welcome}>Xush kelibsiz, {fullName}!</h1>
      <p style={s.desc}>Quyidagi bo'limlardan birini tanlang.</p>
      {SECTIONS.map((section) => (
        <section key={section.title} style={s.section}>
          <h2 style={s.sectionTitle}>{section.title}</h2>
          <div style={s.grid}>
            {section.items.map((item) => (
              <button key={item.path} className="th-card-link" style={s.card} onClick={() => navigate(item.path)}>
                <span style={s.cardIcon}>
                  <Icon name={item.icon} size={20} color="var(--color-on-primary)" />
                </span>
                <span style={s.cardTitle}>{item.title}</span>
                <span style={s.cardDesc}>{item.desc}</span>
                {item.stat && (
                  <span style={s.cardStat} aria-busy={stats === undefined}>
                    <span style={s.cardStatValue}>
                      {stats === undefined ? '…' : Number(stats?.[item.stat]) || 0}
                    </span>
                    <span style={s.cardStatLabel}>{item.statLabel}</span>
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}
    </AdminLayout>
  );
}

const s = {
  welcome: {
    margin: '0 0 4px',
    fontSize: '28px',
    fontWeight: '700',
    color: 'var(--color-text)',
  },
  desc: {
    margin: '0 0 24px',
    fontSize: '14px',
    color: 'var(--color-text-muted)',
  },
  section: { marginBottom: '24px' },
  sectionTitle: { margin: '0 0 12px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '8px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '16px',
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'var(--font-family)',
  },
  cardIcon: {
    display: 'inline-flex',
    width: '40px',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--color-primary)',
    borderRadius: 'var(--radius-btn)',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--color-text)',
  },
  cardStat: { display: 'flex', alignItems: 'baseline', gap: '6px', marginTop: 'auto' },
  cardStatValue: { fontSize: '24px', fontWeight: '700', color: 'var(--color-text)', lineHeight: 1.1 },
  cardStatLabel: { fontSize: '12px', color: 'var(--color-text-muted)' },
  cardDesc: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    lineHeight: '1.4',
  },
};
